/**
 * Maps a MIME type to a Lucide icon, Tailwind color class, bg badge class,
 * and short label. Used in attachment pills, media embeds, and the lightbox header.
 */
import { Archive, File, FileCode, FileSpreadsheet, FileText, ImageIcon, Music, Video } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface FileTypeStyle {
  Icon: LucideIcon;
  /** Tailwind text color class. */
  color: string;
  /** Tailwind background color class for icon badges. */
  bg: string;
  /** Short uppercase label for badge display (e.g. "PDF", "MP4"). */
  label: string;
}

/** Maps a MIME type to display style (icon, color, label). */
export function getFileTypeStyle(mimeType: string): FileTypeStyle {
  if (mimeType.startsWith('image/')) {
    return { Icon: ImageIcon, color: 'text-blue-500', bg: 'bg-blue-500/10', label: 'IMG' };
  }
  if (mimeType.startsWith('video/')) {
    return { Icon: Video, color: 'text-violet-500', bg: 'bg-violet-500/10', label: 'VIDEO' };
  }
  if (mimeType.startsWith('audio/')) {
    return { Icon: Music, color: 'text-green-500', bg: 'bg-green-500/10', label: 'AUDIO' };
  }
  if (mimeType === 'application/pdf') {
    return { Icon: FileText, color: 'text-red-500', bg: 'bg-red-500/10', label: 'PDF' };
  }
  if (
    mimeType === 'application/zip' ||
    mimeType === 'application/x-tar' ||
    mimeType === 'application/gzip' ||
    mimeType === 'application/x-bzip2' ||
    mimeType === 'application/x-7z-compressed' ||
    mimeType === 'application/x-rar-compressed' ||
    mimeType.includes('archive') ||
    mimeType.includes('compressed')
  ) {
    return { Icon: Archive, color: 'text-amber-500', bg: 'bg-amber-500/10', label: 'ZIP' };
  }
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType === 'text/csv') {
    return { Icon: FileSpreadsheet, color: 'text-emerald-500', bg: 'bg-emerald-500/10', label: 'CSV' };
  }
  if (
    mimeType.startsWith('text/') ||
    mimeType.includes('javascript') ||
    mimeType.includes('typescript') ||
    mimeType.includes('json') ||
    mimeType.includes('xml') ||
    mimeType.includes('yaml') ||
    mimeType.includes('graphql') ||
    mimeType.includes('sql')
  ) {
    return { Icon: FileCode, color: 'text-teal-500', bg: 'bg-teal-500/10', label: 'CODE' };
  }
  return { Icon: File, color: 'text-muted-foreground', bg: 'bg-muted', label: 'FILE' };
}

/** Returns a best-guess MIME type from a file path extension.
 *  Superset of `@onestone/shared/mime.ts` — covers code file types for icon selection.
 *  `shared/mime.ts` is the SSOT for HTTP content-type headers; this map adds
 *  the extra extensions needed for UI file-type categorization only. */
export function mimeFromPath(filePath: string): string {
  const ext = filePath.split('.').pop()?.toLowerCase() ?? '';
  const map: Record<string, string> = {
    pdf: 'application/pdf',
    zip: 'application/zip',
    tar: 'application/x-tar',
    gz: 'application/gzip',
    bz2: 'application/x-bzip2',
    '7z': 'application/x-7z-compressed',
    rar: 'application/x-rar-compressed',
    mp4: 'video/mp4',
    webm: 'video/webm',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    mkv: 'video/x-matroska',
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    flac: 'audio/flac',
    m4a: 'audio/mp4',
    csv: 'text/csv',
    json: 'application/json',
    ts: 'text/typescript',
    tsx: 'text/typescript',
    js: 'text/javascript',
    jsx: 'text/javascript',
    py: 'text/x-python',
    sh: 'text/x-sh',
    md: 'text/markdown',
    txt: 'text/plain',
    html: 'text/html',
    css: 'text/css',
    xml: 'text/xml',
    yaml: 'text/yaml',
    yml: 'text/yaml',
    toml: 'text/toml',
    sql: 'text/x-sql',
    rs: 'text/x-rust',
    go: 'text/x-go',
    java: 'text/x-java',
    c: 'text/x-c',
    cpp: 'text/x-c++',
    rb: 'text/x-ruby',
    php: 'text/x-php',
    graphql: 'application/graphql',
    proto: 'text/x-protobuf',
    vue: 'text/x-vue',
    svelte: 'text/x-svelte',
  };
  return map[ext] ?? 'application/octet-stream';
}
